// passwordAuth.js
// PBKDF2-HMAC-SHA256 password verification matching the CMS backend:
// hashlib.pbkdf2_hmac('sha256', password_utf8, salt_bytes, 200_000) -> 32-byte hex digest

const PBKDF2_ITERATIONS = 200000;
const DERIVED_KEY_BITS = 256; // 32 bytes

function hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes;
}

function bytesToHex(bytes) {
    return Array.from(bytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

// Derives a PBKDF2-HMAC-SHA256 hex digest for a password+salt.
export async function derivePasswordHash(password, saltHex) {
    if (!window.crypto?.subtle) {
        throw new Error('Web Crypto SubtleCrypto unavailable (requires HTTPS or localhost)');
    }

    const passwordBytes = new TextEncoder().encode(password);
    const saltBytes = hexToBytes(saltHex);

    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        passwordBytes,
        'PBKDF2',
        false,
        ['deriveBits']
    );

    const derivedBits = await crypto.subtle.deriveBits(
        {
            name: 'PBKDF2',
            salt: saltBytes,
            iterations: PBKDF2_ITERATIONS,
            hash: 'SHA-256'
        },
        keyMaterial,
        DERIVED_KEY_BITS
    );

    return bytesToHex(new Uint8Array(derivedBits));
}

// Verifies a password against a stored PBKDF2 hash/salt pair.
export async function verifyPassword(password, saltHex, expectedHashHex) {
    try {
        const derivedHex = await derivePasswordHash(password, saltHex);
        return derivedHex.toLowerCase() === expectedHashHex.toLowerCase();
    } catch (error) {
        console.error('Password verification failed:', error);
        return false;
    }
}
